// <copyright file="no-purchase-order-dialog.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { html, type TemplateResult } from 'lit';
import { LangFabricationJobActions } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-actions-lang';
import { LitBaseModal } from '../../../webmodule-common/other/ui/modal/modal-factory-lit';

/** What the user chose to do about a job that is going to Awaiting Order with no purchase order. */
export type NoPurchaseOrderChoice = 'assign' | 'proceed';

/**
 * Asks what to do when a job is moved to Awaiting Order without being on a purchase order.
 *
 * Three answers rather than a yes/no confirmation, because "you have no purchase order" has two
 * reasonable responses - fix it now, or carry on because the order is being handled outside the
 * system - and a two-button prompt would force the first into a separate trip through the row menu.
 * Cancelling is a real answer too: the transition has not happened yet.
 */
export class NoPurchaseOrderDialog extends LitBaseModal {
  private choice: NoPurchaseOrderChoice | null = null;

  /** The chosen action, or null when the user cancelled or dismissed the dialog. */
  public static async Execute(): Promise<NoPurchaseOrderChoice | null> {
    const dialog = new NoPurchaseOrderDialog();
    await dialog.showModal();
    return dialog.choice;
  }

  get isFooterVisible(): boolean {
    return true;
  }

  title(): string | TemplateResult {
    return LangFabricationJobActions.noPurchaseOrderTitle();
  }

  bodyTemplate(): TemplateResult {
    return html`<p>${LangFabricationJobActions.noPurchaseOrderExplanation()}</p>
      <p>${LangFabricationJobActions.noPurchaseOrderQuestion()}</p>`;
  }

  footerTemplate(): TemplateResult {
    return html`
      <webmodule-button size="small" variant="primary" @click=${() => this.pick('assign')}>
        ${LangFabricationJobActions.noPurchaseOrderAssign()}
      </webmodule-button>
      <webmodule-button size="small" variant="default" @click=${() => this.pick('proceed')}>
        ${LangFabricationJobActions.noPurchaseOrderProceed()}
      </webmodule-button>
      <webmodule-button size="small" variant="default" @click=${() => this.hideModal()}>
        ${LangFabricationJobActions.noPurchaseOrderCancel()}
      </webmodule-button>
    `;
  }

  private async pick(choice: NoPurchaseOrderChoice): Promise<void> {
    this.choice = choice;
    await this.hideModal();
  }
}
